<template>
  <div class="seletor-produtos">
    <!-- Loading -->
    <div v-if="carregando" class="text-center q-py-xl">
      <q-spinner size="50px" color="primary" />
      <p class="text-grey-6 q-mt-md">Carregando lista de produtos...</p>
    </div>

    <!-- Erro -->
    <div v-else-if="erro" class="text-center q-py-xl">
      <q-icon name="error_outline" size="50px" color="negative" />
      <p class="text-negative q-mt-md">{{ erro }}</p>
      <q-btn
        outline
        color="primary"
        label="Tentar novamente"
        @click="carregarProdutos"
      />
    </div>

    <!-- Lista de produtos -->
    <div v-else>
      <!-- Periodicidade global -->
      <div class="row items-center q-mb-lg q-gutter-md">
        <div class="col-12 col-sm-auto">
          <span class="text-body1 text-weight-medium"
            >Periodicidade padrão:</span
          >
        </div>
        <div class="col-12 col-sm-auto">
          <q-btn-toggle
            v-model="periodicidadeGlobal"
            spread
            unelevated
            toggle-color="primary"
            color="white"
            text-color="primary"
            :options="opcoesPeriodicidade"
            @update:model-value="aplicarPeriodicidadeGlobal"
            class="periodicity-toggle"
          />
        </div>
        <div class="col-12 col-sm-auto q-ml-auto">
          <q-btn
            flat
            dense
            color="grey-6"
            label="Limpar seleção"
            icon="clear_all"
            @click="limparSelecao"
          />
        </div>
      </div>

      <!-- Busca -->
      <q-input
        v-model="busca"
        outlined
        dense
        placeholder="Buscar produto..."
        class="q-mb-md"
        clearable
        bg-color="white"
      >
        <template v-slot:prepend>
          <q-icon name="search" />
        </template>
      </q-input>

      <!-- Grupos de produtos -->
      <div
        v-for="grupo in produtosAgrupados"
        :key="grupo.titulo"
        class="grupo-produtos q-mb-sm"
      >
        <!-- Cabeçalho do grupo -->
        <div class="grupo-header" @click="toggleGrupo(grupo.titulo)">
          <q-icon
            :name="
              gruposAbertos[grupo.titulo] === false
                ? 'chevron_right'
                : 'expand_more'
            "
            size="18px"
            color="grey-7"
          />
          <span
            class="text-caption text-weight-bold text-grey-7 text-uppercase q-ml-xs"
          >
            {{ grupo.titulo }}
          </span>
          <span class="text-caption text-grey-5 q-ml-sm"
            >({{ grupo.produtos.length }})</span
          >
        </div>

        <!-- Itens do grupo -->
        <q-slide-transition>
          <div
            v-show="gruposAbertos[grupo.titulo] !== false"
            class="produtos-lista"
          >
            <div
              v-for="produto in grupo.produtos"
              :key="produto.ca_modelo"
              :class="[
                'produto-linha',
                { 'produto-linha--preenchido': produto._quantidade > 0 },
              ]"
            >
              <!-- Imagem -->
              <q-img
                :src="produto.url_imagem"
                class="produto-img"
                fit="contain"
                no-spinner
                @click.stop="abrirImagem(produto)"
              >
                <template v-slot:error>
                  <div class="absolute-full flex flex-center bg-grey-2">
                    <q-icon
                      name="image_not_supported"
                      size="16px"
                      color="grey-5"
                    />
                  </div>
                </template>
                <div class="img-zoom-hint absolute-full flex flex-center">
                  <q-icon name="zoom_in" size="16px" color="white" />
                </div>
              </q-img>

              <!-- Nome e CA -->
              <div class="produto-info">
                <span class="text-body2 text-weight-medium">{{
                  produto.nome_produto
                }}</span>
                <span class="text-caption text-grey-6 q-ml-sm"
                  >CA: {{ produto.ca_modelo }}</span
                >
              </div>

              <!-- Quantidade -->
              <div class="produto-quantidade">
                <q-input
                  v-model.number="produto._quantidade"
                  type="number"
                  outlined
                  dense
                  placeholder="Qtd."
                  min="0"
                  bg-color="white"
                  style="width: 90px"
                />
              </div>
            </div>
          </div>
        </q-slide-transition>
      </div>

      <!-- Resumo -->
      <div
        v-if="produtosComQuantidade.length > 0"
        class="q-mt-md seletor-resumo"
      >
        <q-banner rounded class="bg-primary-soft">
          <template v-slot:avatar>
            <q-icon name="check_circle" color="primary" />
          </template>
          <span class="text-body2">
            <strong
              >{{ produtosComQuantidade.length }} produto(s) com quantidade
              informada</strong
            >
          </span>
        </q-banner>
      </div>
    </div>

    <!-- Dialog de imagem ampliada -->
    <q-dialog v-model="imagemAmpliada.aberta" maximized>
      <q-card class="img-dialog">
        <q-bar class="bg-dark text-white">
          <span class="text-body2 ellipsis">{{ imagemAmpliada.nome }}</span>
          <q-space />
          <q-btn dense flat icon="close" v-close-popup />
        </q-bar>
        <q-card-section
          class="flex flex-center"
          style="height: calc(100% - 40px)"
        >
          <q-img
            :src="imagemAmpliada.url"
            fit="contain"
            style="max-height: 100%; max-width: 100%"
          />
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import { buscarProdutosParaSelecao } from "../services/vendaApiService";

export default {
  name: "SelecionarMaquina",
  emits: ["update:modelValue"],
  setup(_, { emit }) {
    const produtos = ref([]);
    const carregando = ref(false);
    const erro = ref(null);
    const busca = ref("");
    const periodicidadeGlobal = ref("mensal");

    const opcoesPeriodicidade = [
      { label: "Diário", value: "diario" },
      { label: "Semanal", value: "semanal" },
      { label: "Mensal", value: "mensal" },
      { label: "Anual", value: "anual" },
    ];

    async function carregarProdutos() {
      carregando.value = true;
      erro.value = null;
      try {
        const lista = await buscarProdutosParaSelecao();
        produtos.value = lista.map((p) => ({
          ...p,
          _quantidade: null,
          _periodicidade: periodicidadeGlobal.value,
        }));
      } catch (e) {
        erro.value =
          "Não foi possível carregar os produtos. Verifique a conexão.";
      } finally {
        carregando.value = false;
      }
    }

    const gruposAbertos = ref({});
    const imagemAmpliada = ref({ aberta: false, url: "", nome: "" });

    function abrirImagem(produto) {
      if (!produto.url_imagem) return;
      imagemAmpliada.value = {
        aberta: true,
        url: produto.url_imagem,
        nome: produto.nome_produto,
      };
    }

    function toggleGrupo(titulo) {
      gruposAbertos.value[titulo] =
        gruposAbertos.value[titulo] === false ? true : false;
    }

    const produtosFiltrados = computed(() => {
      const lista = [...produtos.value].sort((a, b) =>
        a.nome_produto.localeCompare(b.nome_produto, "pt-BR")
      );
      if (!busca.value) return lista;
      const termo = busca.value.toLowerCase();
      return lista.filter(
        (p) =>
          p.nome_produto.toLowerCase().includes(termo) ||
          String(p.ca_modelo).toLowerCase().includes(termo)
      );
    });

    const produtosAgrupados = computed(() => {
      const mapa = {};
      for (const p of produtosFiltrados.value) {
        const titulo = p.nome_produto.split(" ")[0].toUpperCase();
        if (!mapa[titulo]) mapa[titulo] = [];
        mapa[titulo].push(p);
      }
      return Object.keys(mapa)
        .sort((a, b) => a.localeCompare(b, "pt-BR"))
        .map((titulo) => ({ titulo, produtos: mapa[titulo] }));
    });

    const produtosComQuantidade = computed(() =>
      produtos.value.filter((p) => p._quantidade > 0)
    );

    function aplicarPeriodicidadeGlobal(valor) {
      produtos.value.forEach((p) => {
        p._periodicidade = valor;
      });
    }

    function limparSelecao() {
      produtos.value.forEach((p) => {
        p._quantidade = null;
        p._periodicidade = periodicidadeGlobal.value;
      });
    }

    // Emite apenas os produtos com quantidade > 0
    watch(
      produtos,
      () => {
        emit(
          "update:modelValue",
          produtosComQuantidade.value.map((p) => ({
            nome_produto: p.nome_produto,
            ca_modelo: p.ca_modelo,
            quantidade: p._quantidade,
            periodicidade: p._periodicidade,
          }))
        );
      },
      { deep: true }
    );

    onMounted(carregarProdutos);

    return {
      produtos,
      carregando,
      erro,
      busca,
      periodicidadeGlobal,
      opcoesPeriodicidade,
      produtosAgrupados,
      gruposAbertos,
      toggleGrupo,
      imagemAmpliada,
      abrirImagem,
      produtosComQuantidade,
      carregarProdutos,
      aplicarPeriodicidadeGlobal,
      limparSelecao,
    };
  },
};
</script>

<style scoped>
.grupo-produtos {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.grupo-header {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  background-color: #f5f5f5;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #e0e0e0;
}

.grupo-header:hover {
  background-color: #eeeeee;
}

.produtos-lista {
  overflow: hidden;
}

.produto-linha {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s;
}

.produto-linha:last-child {
  border-bottom: none;
}

.produto-linha--preenchido {
  background-color: rgba(25, 118, 210, 0.06);
}

.produto-img {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #eee;
  cursor: pointer;
  position: relative;
}

.produto-img .img-zoom-hint {
  opacity: 0;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 4px;
  transition: opacity 0.15s;
}

.produto-img:hover .img-zoom-hint {
  opacity: 1;
}

.img-dialog {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #111;
}

.produto-info {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.produto-quantidade {
  flex-shrink: 0;
}

.periodicity-toggle {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.seletor-resumo .bg-primary-soft {
  background-color: rgba(var(--q-primary-rgb, 25, 118, 210), 0.08);
  border: 1px solid rgba(var(--q-primary-rgb, 25, 118, 210), 0.2);
}
</style>
